import { Injectable, inject } from '@angular/core';
import { User } from '../classes/user';
import { RealmType } from '../enums/realm-type.enum';
import { ServerPermission } from '../enums/server-permission.enum';
import { LoginService } from './login.service';

export enum Entity {
  AccessGroups = 'ACCESS_GROUPS',
  Applications = 'APPLICATIONS',
  Blocking = 'BLOCKING',
  Directories = 'DIRECTORIES',
  Suppliers = 'SUPPLIERS',
  Clients = 'CLIENTS',
  Accounts = 'ACCOUNTS',
  SubrealmAccounts = 'SUBREALM_ACCOUNTS',
  EnterpriseDetails = 'DETAILS',
  Usage = 'USAGE',
  Domains = 'DOMAINS',
  Invitations = 'INVITATIONS',
  Support = 'SUPPORT',
  Audit = 'AUDIT',
  Sales = 'SALES',

  EnterpriseAdmin = 'ENTERPRISE_ADMIN',
}

@Injectable({
  providedIn: 'root'
})
export class PermissionService {
  private loginService = inject(LoginService);

  private readonly permissionMatrix: { [key in Entity]: Rule } = {
    [Entity.EnterpriseAdmin]: AccessRule.realms(RealmType.Enterprise),

    [Entity.AccessGroups]: AccessRule.requiredPermissions(ServerPermission.IdpAccess),
    [Entity.Applications]: AccessRule.somePermissions(ServerPermission.IdpAccess, ServerPermission.IdpClients),
    [Entity.Blocking]: AccessRule.requiredPermissions(ServerPermission.Blocking),
    [Entity.Directories]: AccessRule.requiredPermissions(ServerPermission.Directories),
    [Entity.Suppliers]: AccessRule.realms(RealmType.Idaas),
    [Entity.Clients]: AccessRule.requiredPermissions(ServerPermission.IdpClients),
    [Entity.Accounts]: AccessRule.requiredPermissions(ServerPermission.Accounts),
    [Entity.Sales]: AccessRule.requiredPermissions(ServerPermission.Sales),
    [Entity.SubrealmAccounts]: AccessRule.realms(RealmType.Idaas).requiredPermissions(ServerPermission.Accounts)
        .or(AccessRule.realms(RealmType.Idaas).requiredPermissions(ServerPermission.Sales)),
    [Entity.EnterpriseDetails]: AccessRule.realms(RealmType.Enterprise).requiredPermissions(ServerPermission.Details),
    [Entity.Usage]: AccessRule.realms(RealmType.Enterprise).requiredPermissions(ServerPermission.Reports),
    [Entity.Domains]: AccessRule.realms(RealmType.Enterprise).requiredPermissions(ServerPermission.Details),
    [Entity.Invitations]: AccessRule.realms(RealmType.Enterprise).requiredPermissions(ServerPermission.Details),
    [Entity.Support]: AccessRule.somePermissions(ServerPermission.Support),
    [Entity.Audit]: AccessRule.requiredPermissions(ServerPermission.Audit),
  };

  public canManage(entity: Entity): boolean {
    return this.permissionMatrix[entity].authorised(this.loginService.user);
  }
}

abstract class Rule {
  public abstract authorised(user: User): boolean;

  public and(rule: Rule): Rule {
    if (rule instanceof MultipleAccessRule) {
      return rule.and(this);
    } else {
      return new MultipleAccessRule([this, rule]);
    }
  }

  public or(rule: Rule): Rule {
    if (rule instanceof AtLeastOneAccessRule) {
      return rule.or(this);
    } else {
      return new AtLeastOneAccessRule([this, rule]);
    }
  }
}

class MultipleAccessRule extends Rule {
  private readonly rules: Rule[];

  constructor(rules: Rule[]) {
    super();
    this.rules = rules;
  }

	public authorised(user: User): boolean {
    return this.rules.every(rule => rule.authorised(user));
  }

  public and(rule: Rule): Rule {
    this.rules.push(rule);
    return this;
  }
}

class AtLeastOneAccessRule extends Rule {
  private readonly rules: Rule[];

  constructor(rules: Rule[]) {
    super();
    this.rules = rules;
  }

	public authorised(user: User): boolean {
    return this.rules.some(rule => rule.authorised(user));
  }

  public or(rule: Rule): Rule {
    this.rules.push(rule);
    return this;
  }
}


class AccessRule extends Rule {
  private realmsConstraint?: Constraint;
  private permissionsConstraint?: Constraint;

  /**
   * This access rule requires one or more realms.
   *
   * @param realms The required realms. The user having any one of these realms is sufficient.
   * @returns The access rule, for method chaining
   */
  public realms(...realms: RealmType[]): AccessRule {
    this.realmsConstraint = new RealmsConstraint(realms);
    return this;
  }

  /**
   * This access rule requires one or more realms.
   *
   * @param realms The required realms. The user having any one of these realms is sufficient.
   * @returns The access rule, for method chaining
   */
  public static realms(...realms: RealmType[]): AccessRule {
    const rule = new AccessRule();
    return rule.realms(...realms);
  }

  /**
   * This access rule requires multiple permissions.
   *
   * @param perms The required permissions. The user must have all of these.
   * @returns The access rule, for method chaining
   */
  public requiredPermissions(...perms: ServerPermission[]): AccessRule {
    this.permissionsConstraint = new RequiredPermissionsConstraint(perms);
    return this;
  }

  /**
   * This access rule requires multiple permissions.
   *
   * @param perms The required permissions. The user must have all of these.
   * @returns The access rule, for method chaining
   */
  public static requiredPermissions(...perms: ServerPermission[]): AccessRule {
    const  rule = new AccessRule();
    return rule.requiredPermissions(...perms);
  }

  /**
   * This access rule requires at least one of a set of permissions.
   *
   * @param perms The required permissions. The user must have at least one of these, but does not require all of them.
   * @returns The access rule, for method chaining
   */
  public somePermissions(...perms: ServerPermission[]): AccessRule {
    this.permissionsConstraint = new SomePermissionsConstraint(perms);
    return this;
  }

  /**
   * This access rule requires at least one of a set of permissions.
   *
   * @param perms The required permissions. The user must have at least one of these, but does not require all of them.
   * @returns The access rule, for method chaining
   */
  public static somePermissions(...perms: ServerPermission[]): AccessRule {
    const  rule = new AccessRule();
    return rule.somePermissions(...perms);
  }

  /**
   * Verify that a user satisfies this rule's constraints.
   *
   * @param user The user to check
   * @returns Whether the user satisfies all constraints.
   */
  public authorised(user: User): boolean {
    if (this.realmsConstraint && !this.realmsConstraint.authorised(user)) {
      return false;
    }

    if (this.permissionsConstraint && !this.permissionsConstraint.authorised(user)) {
      return false;
    }

    return true;
  }

}

interface Constraint {
  authorised(user: User): boolean
}

class RealmsConstraint implements Constraint {
  constructor(private realms: RealmType[]) {
  }

  public authorised(user: User): boolean {
    return this.realms.includes(user.currentRealm.type);
  }
}

class RequiredPermissionsConstraint implements Constraint {
  constructor(private permissions: ServerPermission[]) {
  }

  public authorised(user: User): boolean {
    return this.permissions.every(perm => user.permissions?.includes(perm));
  }
}

class SomePermissionsConstraint implements Constraint {
  constructor(private permissions: ServerPermission[]) {
  }

  public authorised(user: User): boolean {
    return this.permissions.some(perm => user.permissions?.includes(perm));
  }
}
